import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useNavigate } from 'react-router';
import { SAGLIK_KISIT_ISARETI_ETIKETI, type SaglikKisitIsareti } from '@isg/validation';
import { useSaglikKanaatKaydet, useSaglikOptions } from '../api/queries';
import { QueryStatus } from './parts';
import { ANLAM } from '../theme';

/**
 * Kanaat kayıt formu (F3B·S8 — Kapsam §16.11).
 *
 * **SİSTEMDE TUTULAN: KANAAT** — tanı, bulgu, tetkik SONUCU alanı YOKTUR ve
 * eklenmez (S1). Beş tetkik kutusu **"yapıldı mı" İŞARETİDİR, sonuç değil**
 * (J/B) — yanına değer alanı koymak modülün sınırını deler.
 *
 * KISIT KALEMLERİ İKİ YÖNLÜDÜR (gezinti artçısı 3 / B7 — Kapsam §16.11/14):
 * kalem nötr bir sorudur, hekim her kalem için **uygun / uygun değil**
 * işaretler; boş bırakılan kalem **sorulmamış** sayılır ve gönderilmez.
 * Kural SUNUCUDADIR (`kisitli` → ≥1 "uygun değil"; `uygun` → "uygun değil"
 * yok, "uygun" serbest; 422). Ekran ikinci kural yazmaz — sınıfı okur ve
 * yalnız açıklama cümlesini seçer.
 *
 * GEÇERLİLİK BİTİŞİ BOŞ BIRAKILABİLİR: sistem periyottan TÜRETİR (S6).
 * Türetilemezse uç 422 verir ve mesaj "tarihi raporun üstünden girin" der.
 */
export function KanaatKayitScreen(): React.JSX.Element {
  const options = useSaglikOptions();
  const kaydet = useSaglikKanaatKaydet();
  const navigate = useNavigate();

  const [personId, setPersonId] = useState<string | null>(null);
  const [workplaceId, setWorkplaceId] = useState<string | null>(null);
  const [gorevMetni, setGorevMetni] = useState('');
  const [muayeneTuruRefId, setMuayeneTuruRefId] = useState<string | null>(null);
  const [muayeneTarihi, setMuayeneTarihi] = useState('');
  const [gecerlilikBitis, setGecerlilikBitis] = useState('');
  const [uygunlukKarariRefId, setUygunlukKarariRefId] = useState<string | null>(null);
  const [kisitIsaretleri, setKisitIsaretleri] = useState<Record<string, SaglikKisitIsareti>>({});
  const [hekimAd, setHekimAd] = useState('');
  const [notlar, setNotlar] = useState('');
  const [tetkikler, setTetkikler] = useState({
    rontgen: false,
    isitme: false,
    sft: false,
    kan: false,
    idrar: false,
  });

  const kararSinifi = useMemo(
    () =>
      options.data?.uygunlukKararlari.find((k) => k.id === uygunlukKarariRefId)?.kararSinifi ??
      null,
    [options.data, uygunlukKarariRefId],
  );
  const kisitler = Object.entries(kisitIsaretleri).map(([kisitRefId, isaret]) => ({
    kisitRefId,
    isaret,
  }));

  const gonder = (): void => {
    if (!personId || !workplaceId || !muayeneTuruRefId || !uygunlukKarariRefId) return;
    kaydet.mutate(
      {
        govde: {
          personId,
          workplaceId,
          gorevMetni: gorevMetni || null,
          muayeneTuruRefId,
          muayeneTarihi,
          gecerlilikBitis: gecerlilikBitis || null,
          uygunlukKarariRefId,
          kisitler,
          hekimAd: hekimAd || null,
          tetkikler,
          notlar: notlar || null,
        },
      },
      { onSuccess: (sonuc) => void navigate(`/saglik/${sonuc.id}`) },
    );
  };

  return (
    <Stack maw={720}>
      <Title order={3}>Yeni sağlık kanaati</Title>
      <QueryStatus queries={[options]} />
      <Alert color={ANLAM.bilgi} variant="light">
        Sisteme <b>yalnız kanaat</b> girilir: Ek-2’nin içi, bulgular ve tetkik <b>sonuçları</b>{' '}
        tutulmaz. Aşağıdaki tetkik kutuları “yapıldı mı” işaretidir.
      </Alert>
      <Select
        label="Kişi"
        searchable
        required
        data-testid="kanaat-kisi"
        data={(options.data?.personeller ?? []).map((p) => ({ value: p.id, label: p.adSoyad }))}
        value={personId}
        onChange={setPersonId}
      />
      <Select
        label="İşyeri (kanaatin verildiği)"
        searchable
        required
        data-testid="kanaat-isyeri"
        data={(options.data?.workplaces ?? []).map((w) => ({
          value: w.id,
          label: `${w.companyAd} — ${w.ad}`,
        }))}
        value={workplaceId}
        onChange={setWorkplaceId}
      />
      <TextInput
        label="Görev (bilgi)"
        description="Kanaat kişiye asılıdır; görev bilgi olarak kayıtta durur."
        value={gorevMetni}
        onChange={(e) => setGorevMetni(e.currentTarget.value)}
      />
      <Select
        label="Muayene türü"
        required
        data-testid="kanaat-tur"
        data={(options.data?.muayeneTurleri ?? []).map((t) => ({ value: t.id, label: t.ad }))}
        value={muayeneTuruRefId}
        onChange={setMuayeneTuruRefId}
      />
      <TextInput
        label="Muayene tarihi"
        type="date"
        required
        data-testid="kanaat-tarih"
        value={muayeneTarihi}
        onChange={(e) => setMuayeneTarihi(e.currentTarget.value)}
      />
      <TextInput
        label="Geçerlilik bitişi (boş bırakılırsa periyottan hesaplanır)"
        type="date"
        data-testid="kanaat-bitis"
        value={gecerlilikBitis}
        onChange={(e) => setGecerlilikBitis(e.currentTarget.value)}
      />
      <Select
        label="Uygunluk kararı"
        required
        data-testid="kanaat-karar"
        data={(options.data?.uygunlukKararlari ?? []).map((k) => ({ value: k.id, label: k.ad }))}
        value={uygunlukKarariRefId}
        onChange={setUygunlukKarariRefId}
      />
      {uygunlukKarariRefId && (
        <Stack gap={4} data-testid="kanaat-kisitlar">
          <Text fw={500} size="sm">
            Çalışma koşulları
          </Text>
          {/* B7 — kalem başına ÜÇ HÂL: boş (sorulmadı) · uygun · uygun değil.
              Kısıt olan yalnız "uygun değil"dir; "uygun" işareti belgede
              olumlu cümle olarak basılır. Sunucu guard'ı aynı ayrımı okur. */}
          <Text size="xs" c="dimmed">
            {kararSinifi === 'kisitli'
              ? '“Kısıtlı uygundur” kararında en az bir kalem “uygun değil” işaretlenir; işaretlenmeyen kalem sorulmamış sayılır.'
              : kararSinifi === 'uygun'
                ? 'Bu kararda “uygun değil” işareti verilemez; “uygun” işaretli kalemler belgede olumlu ifadeyle yazılır.'
                : 'İşaretlenmeyen kalem sorulmamış sayılır ve belgede görünmez.'}
          </Text>
          <Table>
            <Table.Tbody>
              {(options.data?.kisitlar ?? []).map((k) => (
                <Table.Tr key={k.id} data-testid={`kanaat-kisit-satiri-${k.id}`}>
                  <Table.Td>{k.ad}</Table.Td>
                  <Table.Td>
                    <SegmentedControl
                      size="xs"
                      aria-label={k.ad}
                      data-testid={`kanaat-kisit-${k.id}`}
                      value={kisitIsaretleri[k.id] ?? ''}
                      onChange={(deger) =>
                        setKisitIsaretleri((onceki) => {
                          const sonraki = { ...onceki };
                          if (deger === 'uygun' || deger === 'uygun_degil') sonraki[k.id] = deger;
                          else delete sonraki[k.id];
                          return sonraki;
                        })
                      }
                      data={[
                        { value: '', label: 'Sorulmadı' },
                        { value: 'uygun', label: SAGLIK_KISIT_ISARETI_ETIKETI.uygun },
                        { value: 'uygun_degil', label: SAGLIK_KISIT_ISARETI_ETIKETI.uygun_degil },
                      ]}
                    />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      )}
      <TextInput
        label="Hekim (dış hekim adı)"
        description="Hekim sistemde kayıtlıysa kanaati kendisi girer; değilse adı buraya yazılır."
        value={hekimAd}
        onChange={(e) => setHekimAd(e.currentTarget.value)}
      />
      <Checkbox.Group
        label="Yapılan tetkikler (yapıldı mı — SONUÇ değil)"
        value={Object.entries(tetkikler)
          .filter(([, v]) => v)
          .map(([k]) => k)}
        onChange={(secili) =>
          setTetkikler({
            rontgen: secili.includes('rontgen'),
            isitme: secili.includes('isitme'),
            sft: secili.includes('sft'),
            kan: secili.includes('kan'),
            idrar: secili.includes('idrar'),
          })
        }
      >
        <Group mt="xs" data-testid="kanaat-tetkikler">
          <Checkbox value="rontgen" label="Röntgen" />
          <Checkbox value="isitme" label="İşitme" />
          <Checkbox value="sft" label="SFT" />
          <Checkbox value="kan" label="Kan" />
          <Checkbox value="idrar" label="İdrar" />
        </Group>
      </Checkbox.Group>
      {/* S5 — SINIRIN TEK GEDİĞİ: notlar serbest metindir ve kimse onu
          denetlemez. Şerh KARAR ANINDA görünür (M1 istisnası: bu bir kavram
          açıklaması değil, yazarken verilen bir uyarıdır). */}
      <Textarea
        label="Notlar"
        description="Tanı, bulgu ve tetkik sonucu yazılmaz — sistemde yalnız kanaat tutulur."
        value={notlar}
        onChange={(e) => setNotlar(e.currentTarget.value)}
      />
      {kaydet.isError && (
        <Alert color={ANLAM.engel} data-testid="kanaat-hata">
          {kaydet.error.message}
        </Alert>
      )}
      <Group>
        <Button data-testid="kanaat-kaydet" loading={kaydet.isPending} onClick={gonder}>
          Kaydet
        </Button>
        <Button variant="default" onClick={() => void navigate('/saglik')}>
          Vazgeç
        </Button>
      </Group>
    </Stack>
  );
}
